import { lazy } from "react";
import { PERMISSION } from "../../constants/permission";
import { NAVIGATE_TYPE } from "@/constants/app";
import { COMPANY_SUPPORTS } from "@/constants/companys";
import { iTradeEnv } from "@/configurations/app-configs/iTrade.env";
import { PATH } from "@/constants/paths";
import { isMobile } from "@/helpers/global";
import { isProduction } from "@/helpers/global/env.func";

import tradeIcon from "../../assets/images/icons/nav_icon_trade_normal.png";
import marketIcon from "../../assets/images/icons/nav_icon_market_normal.png";
import placeOrderIcon from "../../assets/images/icons/nav_icon_order_normal.png";
import accountIcon from "../../assets/images/icons/nav_icon_acc_normal.png";
import corpActionIcon from "../../assets/images/icons/nav_icon_corp._normal.png";
import ipoIcon from "../../assets/images/icons/nav_icon_ipo_normal.png";
import virtualIcon from "../../assets/images/icons/nav_icon_virtual_normal.png";
import forumIcon from "../../assets/images/icons/nav_icon_forum_normal.png";
import fundIcon from "../../assets/images/icons/nav_icon_cashmanage_normal.png";
import eStatement from "../../assets/images/icons/nav_icon_e-statements_normal.png";
import eMessage from "../../assets/images/icons/nav_icon_e-message_normal.png";

const original = {
  tradePage: !isMobile()
    ? lazy(() => import("@/pages/Trade/Trade"))
    : lazy(() => import("@/pages/Trade/Trade.mobile")),
  ipoPage: !isMobile()
    ? lazy(() => import("@/pages/IPO/IPO"))
    : lazy(() => import("@/pages/IPO/IPO.mobile")),
  myAccountPage: !isMobile()
    ? lazy(() => import("@/pages/MyAccount/MyAccount"))
    : lazy(() => import("@/pages/MyAccount/MyAccount.mobile")),
  changePasswordPage: !isMobile()
    ? lazy(() => import("@/pages/Authentication/ChangePassword/ChangePassword"))
    : lazy(() =>
        import("@/pages/Authentication/ChangePassword/ChangePassword.mobile")
      ),
  marketPage: !isMobile()
    ? lazy(() => import("@/pages/Market/Market"))
    : lazy(() => import("@/pages/Market/Market.mobile")),
};

const rewrites = {
  tradePage: isMobile()
    ? lazy(() =>
        import(`@/companys/${iTradeEnv.company}/pages/Trade/Trade.mobile`)
      )
    : lazy(() => import(`@/companys/${iTradeEnv.company}/pages/Trade/Trade`)),
  ipoPage: isMobile()
    ? lazy(() => import("@/pages/IPO/IPO.mobile"))
    : lazy(() => import(`@/companys/${iTradeEnv.company}/pages/IPO/IPO`)),
  myAccountPage: isMobile()
    ? lazy(() =>
        import(
          `@/companys/${iTradeEnv.company}/pages/MyAccount/MyAccount.mobile`
        )
      )
    : lazy(() =>
        import(`@/companys/${iTradeEnv.company}/pages/MyAccount/MyAccount`)
      ),
  changePasswordPage: isMobile()
    ? lazy(() =>
        import("@/pages/Authentication/ChangePassword/ChangePassword.mobile")
      )
    : lazy(() =>
        import(
          `@/companys/${iTradeEnv.company}/pages/Authentication/ChangePassword/ChangePassword`
        )
      ),
  marketPage: isMobile()
    ? lazy(() =>
        import(`@/companys/${iTradeEnv.company}/pages/Market/Market.mobile`)
      )
    : lazy(() => import(`@/companys/${iTradeEnv.company}/pages/Market/Market`)),
};

const tradePage = iTradeEnv?.pages?.trade?.rewrite
  ? rewrites.tradePage
  : original.tradePage;
const loginPage = lazy(() => import("@/pages/Authentication/Login/Login"));
const registrationPage = lazy(() =>
  import("@/pages/Authentication/Registration/Registration")
);
const forgotPasswordPage = lazy(() =>
  import("@/pages/Authentication/ForgotPassword/ForgotPassword")
);
const changePasswordPage = iTradeEnv?.pages?.change_password?.rewrite
  ? rewrites.changePasswordPage
  : original.changePasswordPage;
const marketPage = iTradeEnv?.pages?.market?.rewrite
  ? rewrites.marketPage
  : original.marketPage;
const placeOrderPage = !isMobile()
  ? lazy(() => import("@/pages/PlaceOrder/PlaceOrder"))
  : lazy(() => import("@/pages/PlaceOrder/PlaceOrder.mobile"));
const myAccountPage = iTradeEnv?.pages?.accountdata?.rewrite
  ? rewrites.myAccountPage
  : original.myAccountPage;
const forumPage = lazy(() => import("@/pages/Forum/Forum"));
const virtualTradingPage = lazy(() =>
  import("@/pages/VirtualTrading/VirtualTrading")
);
const ipoPage = iTradeEnv?.pages?.ipo?.rewrite
  ? rewrites.ipoPage
  : original.ipoPage;
const caPage = !isMobile()
  ? lazy(() => import("@/pages/CorpAction/CorpAction"))
  : lazy(() => import("@/pages/CorpAction/CorpAction.mobile"));
const fundManagementPage = !isMobile()
  ? lazy(() => import("@/pages/FundManagement/FundManagement"))
  : lazy(() => import("@/pages/FundManagement/FundManagement.mobile"));
const eStatementPage = !isMobile()
  ? lazy(() => import("@/pages/EStatement/EStatement"))
  : lazy(() => import("@/pages/EStatement/EStatement.mobile"));
const eMessagePage = lazy(() =>
  import(`@/companys/${iTradeEnv.company}/pages/EMessage/EMessage`)
);
const moreTabPage = lazy(() => import("@/pages/MoreTab/MoreTab"));
const checkAuthPage = lazy(() =>
  import("@/pages/Authentication/CheckAuth/CheckAuth")
);
const resetPasswordPage = lazy(() =>
  import("@/pages/Authentication/ResetPassword/ResetPassword")
);
const bcanPage = lazy(() =>
  import(`@/companys/${iTradeEnv.company}/pages/Authentication/BCAN/BCAN`)
);
const ipRestrictedPage = lazy(() =>
  import(`@/companys/${iTradeEnv.company}/pages/Authentication/ipRestricted/ipRestricted`)
);
export const navigateConfig = [
  {
    id: "trade",
    key: 0,
    label: "menu_trade",
    icon: tradeIcon,
    navLink: "trade",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "market",
    key: 1,
    label: "menu_market",
    icon: marketIcon,
    navLink: "market",
    type: NAVIGATE_TYPE.ITEM,
    permissions: [],
    enabled: true,
    isAuthenticated: false,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "place_order",
    key: 2,
    label: "menu_place_order",
    icon: placeOrderIcon,
    navLink: isMobile() ? "place-order" : "trade",
    type: NAVIGATE_TYPE.ACTION,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: iTradeEnv.company === "ttl" ? false : true,
    companies: [
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "my_account",
    key: 3,
    label: "menu_account",
    icon: accountIcon,
    navLink: "my-account",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "corp_action",
    key: 4,
    label: "menu_corp_action",
    icon: corpActionIcon,
    navLink: "corp-action",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "ipo",
    key: 5,
    label: "menu_ipo",
    icon: ipoIcon,
    navLink: "ipo",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "virtual_trade",
    key: 6,
    label: "menu_virtual_trade",
    icon: virtualIcon,
    navLink: "virtual-trading",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.USER,
    enabled: false,
    isAuthenticated: true,
    isMobileNavDisplay: false,
    companies: [],
  },
  {
    id: "forum",
    key: 7,
    label: "menu_forum",
    icon: forumIcon,
    navLink: "forum",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.USER,
    enabled: false,
    isAuthenticated: true,
    isMobileNavDisplay: false,
    companies: [],
  },
  {
    id: "fund_management",
    key: 8,
    label: "menu_fund_management",
    icon: fundIcon,
    navLink: "fund-management",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.TRADING,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    id: "e_statement",
    key: 9,
    label: "menu_eStatement",
    icon: eStatement,
    navLink: "e-statement",
    type: NAVIGATE_TYPE.ITEM,
    permissions: PERMISSION.USER,
    enabled: true,
    isAuthenticated: true,
    isMobileNavDisplay: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.ECG,
      COMPANY_SUPPORTS.GTJA,
    ],
  },
  // {
  //   id: 'e_message',
  //   key: 10,
  //   label: 'menu_e_message',
  //   icon: eMessage,
  //   navLink: 'e-message',
  //   type: NAVIGATE_TYPE.ITEM,
  //   permissions: PERMISSION.USER,
  //   enabled: true,
  //   isAuthenticated: true,
  //   isMobileNavDisplay: true,
  //   companies: [COMPANY_SUPPORTS.SCB]
  // }
].filter((item) => item.companies.includes(iTradeEnv.company));

export const routerConfig = [
  {
    path: PATH.TRADE,
    page: tradePage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.MARKET_DATA,
    page: marketPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.PLACE_ORDER,
    page: placeOrderPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.ACCOUNT_DATA,
    page: myAccountPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.IPO,
    page: ipoPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.CA,
    page: caPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.FORUM,
    page: forumPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.FORUM_DETAIL,
    page: forumPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.VIRTUAL,
    page: virtualTradingPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.CHANGE_PW,
    page: changePasswordPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.FUND_MANAGEMENT,
    page: fundManagementPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.E_STATEMENT,
    page: eStatementPage,
    isFullLayout: false,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.ECG,
      COMPANY_SUPPORTS.GTJA,
    ],
  },
  // {
  //   path: PATH.E_MESSAGE,
  //   page: eMessagePage,
  //   isFullLayout: false,
  //   companies: [COMPANY_SUPPORTS.SCB]
  // },
  {
    path: PATH.REGISTER,
    page: registrationPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.FORGOT_PW,
    page: forgotPasswordPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.MORE_TAB,
    page: moreTabPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.RESET_PW,
    page: resetPasswordPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.BASIC,
      COMPANY_SUPPORTS.TTL,
    ],
  },
  {
    path: PATH.LOGIN,
    page:
      isProduction() && iTradeEnv.thirdPartyLink
        ? () => (window.location.href = iTradeEnv.thirdPartyLink)
        : loginPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.EIF,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.CMBI,
      COMPANY_SUPPORTS.BASIC,
    ],
  },
  {
    path: PATH.CHECK_AUTH,
    page: checkAuthPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.ECG,
      COMPANY_SUPPORTS.TTL,
      COMPANY_SUPPORTS.CMBI,
    ],
  },
  {
    path: PATH.BCAN,
    page: bcanPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.SCB,
      COMPANY_SUPPORTS.FUBON,
      COMPANY_SUPPORTS.EIF,
    ],
  },
  {
    path: PATH.IPRESTRICTED,
    page: ipRestrictedPage,
    isFullLayout: true,
    companies: [
      COMPANY_SUPPORTS.EIF,
    ],
  },
].filter((item) => item.companies.includes(iTradeEnv.company));
